import type { FC, KeyboardEvent, ReactNode } from 'react'
import { useRef } from 'react'
import styles from './conversation-shell.module.css'
import { conversationTools } from './conversation-tools.js'
import type { ConversationTool } from './conversation-tools.js'

/** Who reads each side (chat makeover #2718): C11 for Chat, C14 for Agents */
const visibility: Record<ConversationTool, string> = {
  Chat: 'Everyone with access to this song',
  Agents: 'Only you see your questions and answers. Suggestions show to everyone who can edit.',
}

export interface ConversationShellProps {
  /** The open tool; the rail and the switch both set it, so they stay in step */
  tool: ConversationTool
  /** Both tools are available for this entity; otherwise there is nothing to switch to */
  canSwitch: boolean
  onSwitch: (tool: ConversationTool) => void
  children: ReactNode
}

/**
 * One shell for the two conversation tools (chat makeover #2718, Option A): Chat and Agents stay separate, with a
 * "Chat | Agents" switch at the top and the line that says who reads each. The switch is a shortcut between the two
 * existing sidebar tools, not a replacement for them in the rail. A tablist: Left and Right move between the tabs,
 * and only the selected tab is in the Tab order (Joni).
 */
export const ConversationShell: FC<ConversationShellProps> = props => {
  const { tool, canSwitch, onSwitch, children } = props
  const tabs = useRef(new Map<ConversationTool, HTMLButtonElement>())

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return
    }
    event.preventDefault()
    const index = conversationTools.indexOf(tool)
    const step = event.key === 'ArrowRight' ? 1 : -1
    const next = conversationTools[(index + step + conversationTools.length) % conversationTools.length]
    onSwitch(next)
    tabs.current.get(next)?.focus()
  }

  return (
    <div className={styles.shell}>
      {canSwitch ? (
        <div role="tablist" aria-label="Conversations" className={styles.switch}>
          {conversationTools.map(name => (
            <button
              key={name}
              ref={element => {
                if (element) {
                  tabs.current.set(name, element)
                }
              }}
              type="button"
              role="tab"
              aria-selected={name === tool}
              tabIndex={name === tool ? 0 : -1}
              className={name === tool ? `${styles.tab} ${styles.selected}` : styles.tab}
              onClick={() => onSwitch(name)}
              onKeyDown={handleKeyDown}
            >
              {name}
            </button>
          ))}
        </div>
      ) : null}
      <p className={styles.visibility}>{visibility[tool]}</p>
      <div className={styles.body}>{children}</div>
    </div>
  )
}
